import css from './DoubleSelect.component.scss';
import html from './DoubleSelect.component.html';
import multiSelectCss from './DoubleSelectMultiSelect.component.scss';
import { MultiSelect } from './MultiSelect';

/**
 * A set of two multiple-selection menus between which the user may move values.
 * 
 * For clarity, the values in the first and second menus are referred to as "excluded" and "included" values, respectively,
 * but there is no functional difference between the two.
 * 
 * Attributes:
 * * `customstyle` - CSS rules to inject into the component.
 * * `excludedfilter` - The `filter` attribute for the excluded side's multi-select.
 * * `excludedsortable` - The `sortable` attribute for the excluded side's multi-select.
 * * `excludedstyle` - The `customstyle` attribute for the excluded side's multi-select.
 * * `includedfilter` - The `filter` attribute for the included side's multi-select.
 * * `includedsortable` - The `sortable` attribute for the included side's multi-select.
 * * `includedstyle` - The `customstyle` attribute for the included side's multi-select.
 * * `single` - The `single` attribute for both sides' multi-select.
 * 
 * Events:
 * * `exclude` - Fired when the user moves values from the include menu to the exclude menu.
 * The event detail contains the moved values as an array of strings.
 * * `excludesort` - Fired when the user sorts the exclude menu.
 * The event detail contains the new, full set of ordered values as an array of strings.
 * * `excludevaluechange` - Fired when the user selects or deselects a value in the exclude menu.
 * The event detail contains the new, full set of selected values in the exclude menu as an array of strings.
 * * `include` - Same as the `exclude` event for the include menu.
 * * `includesort` - Same as the `excludesort` event for the include menu.
 * * `includevaluechange` - Same as the `excludevaluechange` event for the include menu.
 */
export class DoubleSelect extends HTMLElement {
    /**
     * Initializes the element.
     */
    constructor() {
        super();

        const style = document.createElement('style');
        const template = document.createElement('template');

        style.textContent = css;

        template.innerHTML = html;

        this.attachShadow({ mode: 'open' });

        const { shadowRoot } = this;

        shadowRoot.append(style, template.content.cloneNode(true));

        shadowRoot.getElementById('exclude').addEventListener('click', () => this.handleExclude());
        shadowRoot.getElementById('include').addEventListener('click', () => this.handleInclude());
        shadowRoot.getElementById('moveUp').addEventListener('click', () => this.moveSelectedPosition("up"));
        shadowRoot.getElementById('moveDown').addEventListener('click', () => this.moveSelectedPosition("down"));

        for (const side of shadowRoot.querySelectorAll('#excluded, #included')) {
            const select = side.querySelector('multi-select');

            select.addEventListener('sort', event => this.handleSort(side, event));
            select.addEventListener('valuechange', event => this.handleValueChange(side, event));

            select.setAttribute('customstyle', multiSelectCss);

            side.querySelector('.select-all').addEventListener('click', () => handleSelectAll(side));
        }
    }

    /**
     * The current excluded values.
     * 
     * @public
     */
    get excluded() {
        return this.getValues('excluded');
    }

    /**
     * Sets the values in the excluded menu.
     */
    set excluded(excluded) {
        this.setValues('excluded', excluded);
    }

    /**
     * The currently selected excluded values.
     * 
     * @public
     */
    get excludedValue() {
        return this.getValue('excluded');
    }

    /**
     * The current included values.
     * 
     * @public
     */
    get included() {
        return this.getValues('included');
    }

    /**
     * Sets the values in the included menu.
     */
    set included(included) {
        this.setValues('included', included);
    }

    /**
     * The currently selected included values.
     * 
     * @public
     */
    get includedValue() {
        return this.getValue('included');
    }

    /**
     * The attributes observed for changes by the element.
     */
    static get observedAttributes() {
        return [
            'customstyle',
            'excludedfilter',
            'excludedsortable',
            'excludedstyle',
            'includedfilter',
            'includedsortable',
            'includedstyle',
            'single'
        ];
    }

    /**
     * Runs when an attribute observed by the element changes.
     * 
     * @param {string} name The name of the changed attribute.
     * @param {string} _ The old value of the attribute.
     * @param {string} newValue The new value of the attribute.
     */
    attributeChangedCallback(name, _, newValue) {
        switch (name) {
            case 'customstyle':
                this.shadowRoot.getElementById('custom').innerHTML = newValue;
                break;
            case 'excludedfilter':
            case 'includedfilter':
                this.updateFilter(name, newValue);
                break;
            case 'excludedsortable':
            case 'includedsortable':
                this.updateSortable(name, newValue);
                break;
            case 'excludedstyle':
            case 'includedstyle':
                this.updateSideStyle(name, newValue);
                break;
            case 'single':
                this.updateSingle(newValue);
                break;
        }
    }

    /**
     * Retrieves the menu associated with the side that the specified attribute starts with.
     * 
     * @param {`${'excluded' | 'included'}${string}`} attribute The attribute starting with the side whose menu will be retrieved.
     * 
     * @private
     */
    getAttributeSideSelect(attribute) {
        const menu = attribute.slice(0, 'excluded'.length);

        return this.getSelect(menu);
    }

    /**
     * Retrieves the menu from the specified side.
     * 
     * @param {'excluded' | 'included'} side The side whose menu will be retrieved.
     * 
     * @returns {MultiSelect} The menu from the specified side.
     * 
     * @private
     */
    getSelect(side) {
        return this.shadowRoot.querySelector(`#${side} multi-select`);
    }

    /**
     * Retrieves the currently selected values on the specified side.
     * 
     * @param {'excluded' | 'included'} side The side whose currently selected values will be retrieved.
     * 
     * @private
     */
    getValue(side) {
        return this.getSelect(side).value;
    }

    /**
     * Retrieves the values in the menu on the specified side.
     * 
     * @param {'excluded' | 'included'} side The side whose menu will be retrieved.
     * 
     * @private
     */
    getValues(side) {
        return this.getSelect(side).values;
    }

    /**
     * Runs when the user attempts to move values from the included menu to the excluded menu.
     * 
     * If any values were moved,
     * dispatches the `exclude` event with the moved values.
     * 
     * @private
     */
    handleExclude() {
        const values = this.move('included', 'excluded');

        if (values.length) {
            this.dispatchEvent(new CustomEvent('exclude', { detail: values }));
        }
    }

    /**
     * Runs when the user moves values from the excluded menu to the included menu.
     * 
     * If any values were moved,
     * dispatches the `include` event with the moved values.
     * 
     * @private
     */
    handleInclude() {
        const values = this.move('excluded', 'included');

        if (values.length) {
            this.dispatchEvent(new CustomEvent('include', { detail: values }));
        }
    }

    /**
     * Runs when the user sorts the menu within the specified side container.
     * 
     * Dispatches a `*sort` event for the side.
     * 
     * @param {HTMLElement} side The container identifying the menu that was sorted.
     * @param {CustomEvent<string[]>} event The sort event received from the multi-select element.
     * 
     * @private
     */
    handleSort({ id }, { detail }) {
        this.dispatchEvent(new CustomEvent(`${id}sort`, { detail }));
    }

    /**
     * Runs when the user selects new values in the menu within the specified side container.
     * 
     * Dispatches a `*valuechange` event for the side.
     * 
     * @param {HTMLElement} side The container identifying the menu that was sorted.
     * @param {CustomEvent<string[]>} event The sort event received from the multi-select element.
     * 
     * @private
     */
    handleValueChange({ id }, { detail }) {
        this.dispatchEvent(new CustomEvent(`${id}valuechange`, { detail }));

        const showUpDownButtons = detail.length === 1;

        for (const moveButton of this.shadowRoot.querySelectorAll('#moveUp, #moveDown')) {
            moveButton.toggleAttribute('hidden', !showUpDownButtons);
        }
    }

    /**
     * Moves the currently selected values from the specified source side to the specified destination side.
     * 
     * If this element has the `single` attribute,
     * the moved value's position in the source is preserved in the destination.
     * Otherwise, it is appended.
     * 
     * If this element has the `${to}sort` attribute,
     * sorts the destination side's values after moving.
     * 
     * If this element has the `highlighton*` attribute associated with the destination,
     * highlights the values after moving.
     * 
     * @param {'excluded' | 'included'} from The source side.
     * @param {'excluded' | 'included'} to The destination side.
     * 
     * @returns {string[]} The values moved.
     * 
     * @private
     */
     move(from, to) {
        const action = to.slice(0, -1);

        const fromMenu = this.getSelect(from);

        const fromValue = fromMenu.value;
        const fromValues = fromMenu.values;

        const toMenu = this.getSelect(to);

        const toValues = toMenu.values;

        fromMenu.values = fromValues.filter(val => !fromValue.includes(val));

        if (fromValue.length) {
            if (this.hasAttribute('single')) {
                const [value] = fromValue;

                toValues.splice(fromValues.indexOf(value), 0, value);
            } else {
                toValues.push(...fromValue);
            }

            if (this.hasAttribute(`${to}sort`)) {
                toValues.sort();
            }

            toMenu.values = toValues;

            if (this.hasAttribute(`highlighton${action}`)) {
                toMenu.highlight(fromValue);
            }
        }

        return fromValue;
    }

    /**
     * Moves the position of the selected "included" menu item up or down.
     * 
     * @param {'up' | 'down'} direction The direction to move the menu item.
     * 
     * @private
     */
     moveSelectedPosition(direction) {

        const includedMenu = this.getSelect("included");

        const selectedValue = includedMenu.value.find(x => x !== undefined);
        const includedValues = includedMenu.values;

        const origIndex = includedValues.indexOf(selectedValue);
        const newIndex = direction === "up" ? (origIndex <= 1 ? 0 : origIndex - 1) : origIndex + 1; 
        
        const newValues = includedValues.filter(val => !selectedValue.includes(val));
        newValues.splice(newIndex, 0, selectedValue);
        
        this.setValues("included", newValues);
        
        includedMenu.highlight(selectedValue);

        includedMenu.tryScrollToValue(selectedValue);

        this.dispatchEvent(new CustomEvent('includedsort', { detail: this.getValues("included") }));
    }

    /**
     * Sets the available values of the specified menu.
     * 
     * @param {'excluded' | 'included'} side The side whose available values will be set.
     * @param {string[]} values The values to give the menu.
     * 
     * @private
     */
    setValues(side, values) {
        this.getSelect(side).values = values;
    }

    /**
     * Updates the `filter` attribute of the side indicated by the specified attribute to the specified value.
     * 
     * @param {'excludedfilter' | 'includedfilter'} attribute The attribute indicating the side to update.
     * @param {string} filter The filter attribute value.
     * 
     * @private
     */
    updateFilter(attribute, filter) {
        const select = this.getAttributeSideSelect(attribute);

        select.toggleAttribute('filter', typeof filter === 'string');
    }

    /**
     * Updates the `customstyle` attribute of the side indicated by the specified attribute to the specified value.
     * 
     * @param {'excludedcustomstyle' | 'includedcustomstyle'} attribute The attribute indicating the side to update.
     * @param {string} filter The customstyle attribute value.
     * 
     * @private
     */
    updateSideStyle(attribute, style) {
        const select = this.getAttributeSideSelect(attribute);

        select.setAttribute('customstyle', multiSelectCss + style);
    }

    /**
     * Updates the `single` attribute of both sides to the specified value.
     * 
     * @param {string} value The single attribute value.
     * 
     * @private
     */
    updateSingle(value) {
        for (const side of this.shadowRoot.querySelectorAll('#excluded, #included')) {
            side.querySelector('multi-select').toggleAttribute('single', typeof value === 'string');
        }
    }

    /**
     * Updates the `sortable` attribute of the side indicated by the specified attribute to the specified value.
     * 
     * @param {'excludedsortable' | 'includedsortable'} attribute The attribute indicating the side to update.
     * @param {string} sortable The attribute value.
     * 
     * @private
     */
    updateSortable(attribute, sortable) {
        const select = this.getAttributeSideSelect(attribute);

        select.toggleAttribute('sortable', typeof sortable === 'string');
    }
}

/**
 * Runs when the user clicks the Select All button for a menu.
 * 
 * Selects all of the values in the corresponding menu.
 * 
 * @param {HTMLElement} side The container element of the side.
 */
function handleSelectAll(side) {
    side.querySelector('multi-select').highlightAll();
}

customElements.define('double-select', DoubleSelect);
